import { Viewer3dLoadView3dOptions } from '@3ddv/dvm-internal';
import { Viewer3dComponent, Viewer3dService } from '@3ddv/ngx-dvm-internal';
import { Injectable, inject } from '@angular/core';
import { Subscription, take } from 'rxjs';
import { ISectionsPriceRange } from '../../../types/sections-price-range';
import { EventsService } from '../../events/data-access/events.service';

@Injectable({
  providedIn: 'root',
})
export class PanoramicViewerService {
  public viewer3DRef!: Viewer3dComponent;
  private subscriptions: Subscription[] = [];
  private eventService = inject(EventsService);

  /**
   * Initializes the 3D viewer service by setting up the viewer and registering necessary subscriptions.
   * @param viewer The Viewer3dComponent instance to be used and managed.
   */
  public initialize = (viewer: Viewer3dComponent) => {
    const initSubscription = viewer.service.initialize().subscribe(() => this.setupViewer(viewer));
    this.viewer3DRef = viewer;
    this.subscriptions.push(initSubscription);
  };

  /**
   * Cleans up and unsubscribes from all subscriptions when the viewer is no longer needed to avoid memory leaks.
   */
  public cleanSubscriptions = () => {
    this.subscriptions.forEach(s => s.unsubscribe());
  };

  /**
   * Resets the 3D viewer to its initial state.
   */
  public reset = () => {
    this.viewer3DRef.service.reset();
  };

  /**
   * Loads a 3D map into the viewer based on a specified node ID.
   * @param nodeID The identifier of the node (view) to load into the 3D viewer.
   */
  public loadMap3D = (nodeID: Viewer3dLoadView3dOptions['view_id']) => {
    const venue_id = this.eventService.getEventConfiguration()?.venueId;
    if (!nodeID || !venue_id) return;

    const viewData = {
      venue_id,
      view_id: nodeID,
    } satisfies Viewer3dLoadView3dOptions;

    this.viewer3DRef.service.loadView3d(viewData);
  };

  /**
   * Sets up the viewer by configuring viewer flags and subscribing to necessary viewer events.
   * @param viewer The Viewer3dComponent instance to be set up.
   */
  private setupViewer = (viewer: Viewer3dComponent) => {
    this.configureViewerFlags(viewer);
    this.subscribeToLoadSuccess(viewer);
    this.subscribeToLoadMap();
  };

  /**
   * Subscribes to viewer's load success event.
   * @param viewer The Viewer3dComponent instance being observed.
   */
  private subscribeToLoadSuccess = (viewer: Viewer3dComponent) => {
    const subscription = viewer.service.getObservable('load_success').subscribe(() => {
      // this.configureViewerFlags(viewer);
    });
    this.subscriptions.push(subscription);
  };

  /**
   * Initiates the load map process with a default or specified node ID.
   */
  private subscribeToLoadMap = () => {
    // empty map section to be able to load the map
    this.loadMap3D('');
  };

  /**
   * Configures the viewer flags for optimal user experience and viewer performance.
   * @param viewer The Viewer3dComponent instance being configured.
   */
  private configureViewerFlags = (viewer: Viewer3dComponent) => {
    viewer.service.flags.fixed_aspect_ratio = false;
    viewer.service.flags.show_loading_screen = true;
    viewer.service.flags.show_fullscreen_button = false;
  };

  /**
   * Loads the thumbnail image for a given section in a venue.
   *
   * @param venueId - The ID of the venue.
   * @param item - The section item containing details like sectionId and where the thumbnail will be stored.
   */
  public loadThumbnailForSection(venueId: string, item: ISectionsPriceRange): void {
    this.viewer3DRef.service.waitInitialize().subscribe(service3d => {
      this.checkAndLoadThumbnail(service3d, venueId, item);
    });
  }

  /**
   * Checks if a 3D view is available for a given section and loads the thumbnail if available.
   *
   * @param service3d - The 3D service instance.
   * @param venueId - The ID of the venue.
   * @param item - The section item to update with the thumbnail image.
   */
  private checkAndLoadThumbnail(service3d: Viewer3dService, venueId: string, item: ISectionsPriceRange): void {
    service3d
      .checkView3d({ venue_id: venueId, view_id: item.sectionId })
      .pipe(take(1))
      .subscribe(isViewAvailable => {
        if (isViewAvailable) {
          this.loadThumbnail(service3d, venueId, item);
        }
      });
  }

  /**
   * Loads the thumbnail image for the section either immediately if the service is already loaded
   * or waits for the service to load first.
   *
   * @param service3d - The 3D service instance.
   * @param venueId - The ID of the venue.
   * @param item - The section item to update with the thumbnail image.
   */
  private loadThumbnail(service3d: Viewer3dService, venueId: string, item: ISectionsPriceRange): void {
    if (service3d.isLoaded()) {
      this.getThumbnailImage(service3d, item.sectionId, venueId, item);
    } else {
      this.waitForServiceLoad(service3d, item.sectionId, venueId, item);
    }
  }

  /**
   * Fetches the thumbnail image once the 3D service is loaded.
   *
   * @param service3d - The 3D service instance.
   * @param sectionId - The ID of the section.
   * @param venueId - The ID of the venue.
   * @param item - The section item to update with the thumbnail image.
   */
  private getThumbnailImage(
    service3d: Viewer3dService,
    sectionId: string,
    venueId: string,
    item: ISectionsPriceRange
  ): void {
    service3d.getThumbnail({ view_id: sectionId, venue_id: venueId }).subscribe(img => {
      if (typeof img === 'string') {
        item.image = img;
      }
    });
  }

  /**
   * Waits for the 3D service to fully load and then fetches the thumbnail image.
   *
   * @param service3d - The 3D service instance.
   * @param sectionId - The ID of the section.
   * @param venueId - The ID of the venue.
   * @param item - The section item to update with the thumbnail image.
   */
  private waitForServiceLoad(
    service3d: Viewer3dService,
    sectionId: string,
    venueId: string,
    item: ISectionsPriceRange
  ): void {
    service3d
      .getObservable('load_success')
      .pipe(take(1))
      .subscribe(() => {
        this.getThumbnailImage(service3d, sectionId, venueId, item);
      });
  }
}
